import { Button } from "@volca/ui/components/ui/button";
import { FilterChip } from "@volca/ui/components/ui/filter-chip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type {
  PayrollRosterField,
  RosterFieldFilter,
  RosterFieldValueSummary,
} from "@/payroll/roster/types";
import { ROSTER_FILTER_KIND } from "@/payroll/roster/types";
import {
  isRosterFilterActive,
  ROSTER_NO_VALUE_LABEL,
  rosterFilterDetail,
  rosterFilterSummary,
} from "@/payroll/roster/utils";
import {
  BooleanFilterBody,
  OptionsFilterBody,
  RangeFilterBody,
  TextFilterBody,
} from "./filter-bodies";
import { FilterCheckRow } from "./filter-control-parts";

interface RosterFieldFilterChipProps {
  field: PayrollRosterField;
  options: string[];
  summary: RosterFieldValueSummary | undefined;
  filter: RosterFieldFilter;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (filter: RosterFieldFilter) => void;
  onClear: () => void;
}

export const RosterFieldFilterChip = ({
  field,
  options,
  summary,
  filter,
  open,
  onOpenChange,
  onChange,
  onClear,
}: RosterFieldFilterChipProps) => {
  const active = isRosterFilterActive(filter);
  const summaryText = active ? rosterFilterSummary(field, filter) : undefined;
  const bodyProps = { field, options, summary, onChange };
  const canFilterBlank = (summary?.blank ?? 0) > 0 || filter.includeBlank;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        render={
          <FilterChip
            label={field.label}
            value={summaryText}
            active={active}
            onClear={onClear}
            aria-label={undefined}
            title={active ? rosterFilterDetail(field, filter) : undefined}
          />
        }
      />

      <PopoverContent align="start" className="w-72 p-0">
        <Stack className="w-full" align="start" gap={0}>
          <Stack
            direction="row"
            align="center"
            justify="between"
            gap={2}
            className="w-full border-b border-line px-3 py-2"
          >
            <Text
              variant="label"
              className="min-w-0 truncate"
              title={field.label}
            >
              {field.label}
            </Text>
            {active && (
              <Button size="xs" variant="ghost" onClick={onClear}>
                Clear
              </Button>
            )}
          </Stack>

          {filter.kind === ROSTER_FILTER_KIND.options ? (
            <OptionsFilterBody {...bodyProps} filter={filter} />
          ) : filter.kind === ROSTER_FILTER_KIND.boolean ? (
            <BooleanFilterBody {...bodyProps} filter={filter} />
          ) : filter.kind === ROSTER_FILTER_KIND.range ? (
            <RangeFilterBody {...bodyProps} filter={filter} />
          ) : (
            <TextFilterBody {...bodyProps} filter={filter} />
          )}

          {canFilterBlank && (
            <Stack
              align="start"
              gap={0}
              className="w-full border-t border-line p-1"
            >
              <FilterCheckRow
                id={`roster-filter-${field.key}-no-value`}
                label={ROSTER_NO_VALUE_LABEL}
                count={summary?.blank ?? 0}
                checked={filter.includeBlank}
                onToggle={() =>
                  onChange({ ...filter, includeBlank: !filter.includeBlank })
                }
              />
            </Stack>
          )}
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
